import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { isWeb } from '../utils';
import type { RootStackParamList } from '../types/RootStackParamList';

/**
 * 하단탭 호스트(BottomTabNavigator)의 **루트 스택** 라우트 이름. 플랫폼별로 다르다.
 * - native: `Home` (Navigation.native.tsx)
 * - web: `tab` (NavigationWithModalProvider.tsx)
 *
 * ⚠️ 개별 탭(`HOME`, `OVERVIEW`, `LIKED_GOODS` …)은 이 호스트 **안쪽** 라우트라
 * 루트에서 `navigate('HOME')` 하면 어느 네비게이터도 처리하지 못한다. 반드시
 * `{ name: TAB_HOST_ROUTE_NAME, params: { screen: 'HOME', params: … } }` 중첩 형태로 보낸다.
 */
export const TAB_HOST_ROUTE_NAME = isWeb ? 'tab' : 'Home';

/** 하단탭 안의 홈 탭 라우트 이름(HomeTabScreens 의 screenName). */
export const HOME_TAB_ROUTE_NAME = 'HOME';

export const useRootStackNavigation = <
  RouteName extends keyof RootStackParamList,
>() => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList, RouteName>>();

  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else if (isWeb) {
      history.back();
    }
  };

  return {
    ...navigation,
    goBack,
  };
};

export const useRootRoute = <RouteName extends keyof RootStackParamList>() => {
  return useRoute<RouteProp<RootStackParamList, RouteName>>();
};
